import { useCallback, useState } from 'react'

type UseSortOrderStateReturn<T extends string> = readonly [T, (value: T) => void]

export function useSortOrderState<T extends string>(
  storageKey: string,
  defaultValue: T,
  validValues: readonly T[],
): UseSortOrderStateReturn<T> {
  const [sortOrder, setSortOrder] = useState<T>(() =>
    loadSortOrder(storageKey, defaultValue, validValues),
  )

  const updateSortOrder = useCallback(
    (value: T) => {
      setSortOrder(value)
      saveSortOrder(storageKey, value)
    },
    [storageKey],
  )

  return [sortOrder, updateSortOrder] as const
}

function loadSortOrder<T extends string>(
  storageKey: string,
  defaultValue: T,
  validValues: readonly T[],
): T {
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored && isValidSortOrder(stored, validValues)) {
      return stored
    }
  } catch {
    void 0
  }
  return defaultValue
}

function saveSortOrder(storageKey: string, value: string): void {
  try {
    localStorage.setItem(storageKey, value)
  } catch {
    void 0
  }
}

function isValidSortOrder<T extends string>(value: string, validValues: readonly T[]): value is T {
  return (validValues as readonly string[]).includes(value)
}
